import { useDebounce } from "use-debounce";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useSuggestFilterColumnsQuery } from "src/graphql";
import type { Column } from "src/types/visual";
import { useAgentControlContext } from "src/components/explore/context/agent-control-context";

// Module-level constant: a stable empty-array reference used as the default
// when the AI suggestions query has no data. Returning a fresh `[]` literal
// per render invalidates downstream useMemo deps in useConditionSelect,
// causing the entire option-building pipeline to recompute on every render.
const EMPTY_SUGGESTIONS: Column[] = [];

export function useAIColumnSuggestions(args: {
  parentModelId: string | undefined;
  search: string;
  enabled?: boolean;
}) {
  const { appAudienceAgentsFilterPopoverSuggestions } = useFlags();
  const { agentAvailable } = useAgentControlContext();

  const isEnabled =
    agentAvailable &&
    Boolean(appAudienceAgentsFilterPopoverSuggestions) &&
    args.enabled !== false &&
    Boolean(args.parentModelId) &&
    args.search.trim().length > 0;

  const [debouncedSearch] = useDebounce(args.search, 200);

  const { data, isLoading } = useSuggestFilterColumnsQuery(
    {
      parentModelId: args.parentModelId!,
      search: debouncedSearch,
    },
    {
      enabled: isEnabled && debouncedSearch.trim().length > 0,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      cacheTime: 600_000,
      staleTime: Infinity,
    },
  );

  const suggestedColumns: Column[] =
    data?.suggestFilterColumns?.suggestions ?? EMPTY_SUGGESTIONS;

  return {
    suggestedColumns,
    isLoading: isEnabled && isLoading,
    isEnabled,
  };
}
